// ── Template tokenizer (export side) ─────────────────────────────────────────
// Turns a live serialized dashboard into a de-identified v2 template: real metric
// ids → {MET_n} (retaining category+key for identity-keep), real subject ids →
// {PL_n}/{POS_n}/{GRP_n}, drills + note ids stripped, display names tokenized via a
// global pass. See Docs/TEMPLATE_SYSTEM.md §7.1.

import type { ComponentSpec } from "../Processing/types";
import type { GridWidgetType } from "../types";
import { getWidgetSlots, isNoSlots, isDrillSubject, subjectKind } from "../Slots";
import { parseTokens } from "../Utils/resolveTemplate";
import {
  metricToken,
  subjectToken,
  type MetricCategory,
  type SubjectKind,
  type TemplateFileV2,
  type TemplateSlots,
} from "./templateTokens";

export interface SerializedDashboard {
  id: string;
  name: string;
  spec_version: number;
  widgets: Array<{ id: string; type: string; config: Record<string, unknown> }>;
  layout: Array<{ i: string; x: number; y: number; w: number; h: number }>;
}

export interface TokenizeInput {
  serialized: SerializedDashboard;
  /** unique_identifier → {category, name}. */
  metricCatalog: Map<string, { category: MetricCategory; name: string }>;
  /** full subject id ("player-42" | "pos-3" | "group-1") → display name. */
  subjectNames: Map<string, string>;
}

export interface TokenizeResult {
  template: TemplateFileV2;
  warnings: string[];
  /** Real ids/names that survived tokenization — export must be blocked while non-empty. */
  piiViolations: string[];
}

/** Global literal replace of every name → token, longest name first (avoids partial overlap). */
function replaceNamesInSegment(segment: string, names: string[], nameToToken: Map<string, string>): string {
  for (const name of names) {
    segment = segment.split(name).join(nameToToken.get(name)!);
  }
  return segment;
}

/**
 * The name pass over the whole config JSON. It skips the inside of every slicer token
 * `{Slicer.KEY.Variable}`: a key such as GK can equal a position name, and the token must
 * survive the export as typed, because keys copy with `slicer_builder_misc` on import.
 */
function replaceNames(json: string, nameToToken: Map<string, string>): string {
  const names = [...nameToToken.keys()].filter(Boolean).sort((a, b) => b.length - a.length);
  let out = "";
  let cursor = 0;
  for (const token of parseTokens(json)) {
    if (!token.prefixed) continue;
    out += replaceNamesInSegment(json.slice(cursor, token.start), names, nameToToken) + token.raw;
    cursor = token.end;
  }
  return out + replaceNamesInSegment(json.slice(cursor), names, nameToToken);
}

export function tokenizeDashboard(input: TokenizeInput): TokenizeResult {
  const { serialized, metricCatalog, subjectNames } = input;
  const warnings: string[] = [];

  const slots: TemplateSlots = { metrics: {}, subjects: {} };
  const metricIdToToken = new Map<string, string>();
  const subjectIdToToken = new Map<string, string>();
  const nameToToken = new Map<string, string>();
  const counters = { MET: 0, player: 0, position: 0, group: 0 };

  const widgets = serialized.widgets.map((w) => {
    const descriptor = getWidgetSlots(w.type as GridWidgetType);
    // Deep clone so the live dashboard is untouched.
    let config = JSON.parse(JSON.stringify(w.config)) as Record<string, unknown>;

    if (descriptor && !isNoSlots(descriptor)) {
      const spec = config as unknown as ComponentSpec;
      descriptor.stripDrills(spec);
      descriptor.stripPrivate?.(spec);

      // Metrics: real id → {MET_n} (identity retained in the manifest).
      for (const ref of descriptor.metricRefs(spec)) {
        const id = ref.get();
        if (id == null || id === "") continue;
        const realId = String(id);
        let token = metricIdToToken.get(realId);
        if (!token) {
          const meta = metricCatalog.get(realId);
          if (!meta) {
            warnings.push(`Metric "${realId}" not found in the team catalog; it will not resolve on import.`);
            continue;
          }
          token = metricToken(++counters.MET);
          metricIdToToken.set(realId, token);
          slots.metrics[token.slice(1, -1)] = { category: meta.category, key: realId, name: meta.name };
          nameToToken.set(meta.name, token);
        }
        ref.set(token);
      }

      // Subjects: full-team untouched; drills stripped; else → {PL_n}/{POS_n}/{GRP_n}.
      for (const ref of descriptor.subjectRefs(spec)) {
        const val = ref.get();
        if (val == null || val === "full-team") continue;
        if (isDrillSubject(val)) { ref.set("full-team"); continue; }
        const kind = subjectKind(val);
        if (!kind) continue; // unknown — leave as-is (PII assertion will flag if it's an id)
        const realId = String(val);
        let token = subjectIdToToken.get(realId);
        if (!token) {
          const counterKey: SubjectKind = kind;
          token = subjectToken(kind, ++counters[counterKey]);
          subjectIdToToken.set(realId, token);
          slots.subjects[token.slice(1, -1)] = { kind, name: subjectNames.get(realId) };
          const nm = subjectNames.get(realId);
          if (nm) nameToToken.set(nm, token);
        }
        ref.set(token);
      }
    }

    // Global name pass over the whole widget config (titles, selectedSubjectNames,
    // displayName, radar metric_names, body_map title). "ACWR"/"(ACWR)" suffixes
    // survive as literal text adjacent to the metric-name token. A slicer's key is
    // not a name: it is put back after the pass, so the tokens that quote it still resolve.
    const slicerMisc = (config as { slicer_builder_misc?: { key?: unknown } }).slicer_builder_misc;
    const slicerKey = typeof slicerMisc?.key === "string" ? slicerMisc.key : undefined;
    config = JSON.parse(replaceNames(JSON.stringify(config), nameToToken)) as Record<string, unknown>;
    if (slicerKey !== undefined) {
      (config.slicer_builder_misc as { key?: string }).key = slicerKey;
    }

    return { id: w.id, type: w.type, config };
  });

  const template: TemplateFileV2 = {
    spec_version: 2,
    id: serialized.id,
    name: serialized.name,
    widgets,
    layout: serialized.layout,
    slots,
  };

  const piiViolations = assertNoPii(template, subjectNames, warnings);
  return { template, warnings, piiViolations };
}

/** Scan the tokenized template for any surviving team-specific id or roster name. */
function assertNoPii(
  template: TemplateFileV2,
  subjectNames: Map<string, string>,
  warnings: string[],
): string[] {
  const json = JSON.stringify(template.widgets);
  const violations: string[] = [];

  const idHits = json.match(/"(player|pos|group|drill)-\d+"/g);
  if (idHits) violations.push(...[...new Set(idHits)].map((h) => `Untokenized subject/drill id ${h}`));

  // Bare player_card ids: numeric "player_id": 42
  const bareIds = json.match(/"player_id":\s*\d+/g);
  if (bareIds) violations.push(...[...new Set(bareIds)].map((h) => `Untokenized ${h}`));

  // Note ids that survived stripPrivate.
  const noteHits = json.match(/"noteIds":\s*\[[^\]]*\d[^\]]*\]/g);
  if (noteHits) violations.push("Note ids survived stripping");

  // Any current-roster display name still present (free-text titles / overrides).
  for (const name of subjectNames.values()) {
    if (name && name.length > 2 && json.includes(name)) {
      warnings.push(`A title or label may still contain the name "${name}" — review before publishing.`);
    }
  }

  return violations;
}
